import { useEffect, useState } from 'react';

import { Container, Form, Button, Alert, Row, Col } from 'react-bootstrap';
import { useApp } from 'common/AppContext';

import { unwrap } from './helpers';

const initialFields = {
    custom_expected: '',
    custom_steps: '',
    refs: '',
    title: ''
};

export const CreateCase = () => {

    const { api } = useApp();

    const [projects, setProjects] = useState([]);
    const [suites, setSuites] = useState([]);
    const [sections, setSections] = useState([]);

    const [projectId, setProjectId] = useState('');
    const [suiteId, setSuiteId] = useState('');
    const [sectionId, setSectionId] = useState('');

    const [fields, setFields] = useState(initialFields);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    useEffect(() => {

        api.get('/api/testrail/projects')
            .then(d => setProjects(unwrap(d, 'projects')))
            .catch(e => setError(e.err || e.message));

    }, [api]);

    useEffect(() => {

        setSuiteId('');
        setSectionId('');
        setSections([]);

        if (!projectId) {

            setSuites([]);
            return;

        }

        api.get(`/api/testrail/projects/${projectId}/suites`)
            .then(d => setSuites(unwrap(d, 'suites')))
            .catch(e => setError(e.err || e.message));

    }, [api, projectId]);

    useEffect(() => {

        setSectionId('');

        if (!projectId || !suiteId) {

            setSections([]);
            return;

        }

        api.get(`/api/testrail/projects/${projectId}/sections?suite_id=${suiteId}`)
            .then(d => setSections(unwrap(d, 'sections')))
            .catch(e => setError(e.err || e.message));

    }, [api, projectId, suiteId]);

    const update = key => e => setFields(prev => ({ ...prev, [key]: e.target.value }));

    const submit = async e => {

        e.preventDefault();
        setSubmitting(true);
        setError(null);
        setSuccess(null);

        // Only send populated fields. TestRail rejects empty custom fields.
        const payload = Object.fromEntries(
            Object.entries(fields).filter(([, v]) => v && String(v).trim().length)
        );

        try {

            const data = await api.post(`/api/testrail/sections/${sectionId}/cases`, payload);
            setSuccess(`Created case #${data?.id || '?'}: ${data?.title || fields.title}`);
            setFields(initialFields);

        } catch (err) {

            setError(err.err || err.message);

        } finally {

            setSubmitting(false);

        }

    };

    const canSubmit = !submitting && !!sectionId && !!fields.title;

    return (

        <Container className="py-4" style={{ maxWidth: 720 }}>

            <h3>
                Create a TestRail Case
            </h3>
            <p className="text-muted">
                Pick a section, then describe the case.
            </p>

            {error && (
            <Alert dismissible onClose={() => setError(null)} variant="danger">
                {error}
            </Alert>
            )}
            {success && (
            <Alert dismissible onClose={() => setSuccess(null)} variant="success">
                {success}
            </Alert>
            )}

            <Form onSubmit={submit}>

                <Row className="g-2">

                    <Col md={4}>
                        <Form.Group className="mb-3" controlId="tr-cc-project">
                            <Form.Label>
                                Project
                            </Form.Label>
                            <Form.Select required onChange={e => setProjectId(e.target.value)} value={projectId}>
                                <option value="">
                                    Select…
                                </option>
                                {projects.map(p => (
                                    <option key={p.id} value={p.id}>
                                        {p.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>

                    <Col md={4}>
                        <Form.Group className="mb-3" controlId="tr-cc-suite">
                            <Form.Label>
                                Suite
                            </Form.Label>
                            <Form.Select
                                required
                                disabled={!suites.length}
                                onChange={e => setSuiteId(e.target.value)}
                                value={suiteId}
                            >
                                <option value="">
                                    Select…
                                </option>
                                {suites.map(s => (
                                    <option key={s.id} value={s.id}>
                                        {s.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>

                    <Col md={4}>
                        <Form.Group className="mb-3" controlId="tr-cc-section">
                            <Form.Label>
                                Section
                            </Form.Label>
                            <Form.Select
                                required
                                disabled={!sections.length}
                                onChange={e => setSectionId(e.target.value)}
                                value={sectionId}
                            >
                                <option value="">
                                    Select…
                                </option>
                                {sections.map(s => (
                                    <option key={s.id} value={s.id}>
                                        {s.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>

                </Row>

                <Form.Group className="mb-3" controlId="tr-cc-title">
                    <Form.Label>
                        Title
                    </Form.Label>
                    <Form.Control
                        required
                        onChange={update('title')}
                        placeholder="Verify that the dashboard greets the user"
                        value={fields.title}
                    />
                </Form.Group>

                <Form.Group className="mb-3" controlId="tr-cc-refs">
                    <Form.Label>
                        References
                        <small className="text-muted">
                            (optional)
                        </small>
                    </Form.Label>
                    <Form.Control
                        onChange={update('refs')}
                        placeholder="JIRA-123, TICKET-456"
                        value={fields.refs}
                    />
                </Form.Group>

                <Form.Group className="mb-3" controlId="tr-cc-steps">
                    <Form.Label>
                        Steps
                        <small className="text-muted">
                            (optional)
                        </small>
                    </Form.Label>
                    <Form.Control
                        as="textarea"
                        onChange={update('custom_steps')}
                        placeholder="1. Sign in&#10;2. Open the dashboard"
                        rows={4}
                        value={fields.custom_steps}
                    />
                </Form.Group>

                <Form.Group className="mb-3" controlId="tr-cc-expected">
                    <Form.Label>
                        Expected result
                        <small className="text-muted">
                            (optional)
                        </small>
                    </Form.Label>
                    <Form.Control
                        as="textarea"
                        onChange={update('custom_expected')}
                        placeholder="The dashboard greets the user by name."
                        rows={3}
                        value={fields.custom_expected}
                    />
                </Form.Group>

                <Button disabled={!canSubmit} type="submit" variant="primary">
                    {submitting ? 'Creating…' : 'Create case'}
                </Button>

            </Form>

        </Container>

    );

};
